// src/services/booking/checkout.service.js

import UserService from '@/services/user.service';
import AdminService from '@/services/admin.service';
import LyraService from '@/services/lyra.service';
import CartService from '@/services/booking/cart.service';
import { useMainStore } from '@/stores/mainStore';
import { useBookingSystemStore } from '@/stores/bookingSystemStore';

/**
 * Service de gestion des différents types de checkout/paiement
 * Responsable de la gestion des processus de paiement et de validation
 */
class CheckoutService {
    /**
     * Prépare le bookingStore avec les informations communes
     * 
     * @private
     * @param {Object} bookingStore - Le store de réservation
     */
    _prepareBookingStore(bookingStore) {
        const mainStore = useMainStore();
        bookingStore.vip_ins = mainStore?.inscription_vip?.$state;
        bookingStore.id_account = mainStore.authUser.userId;
        bookingStore.generate_uuid();
    }

    /**
     * Recalcule les paniers de chaque course avant l'envoi au backend.
     *
     * @private
     * @param {Object} bookingStore - Le store de réservation
     */
    async _refreshCartsBeforePost(bookingStore) {
        const mainStore = useMainStore();
        const vipIns = mainStore.inscription_vip;
        const groupe = mainStore.bookingSystem?.groupe?.$state;

        for (const inscription of bookingStore.inscriptions || []) {
            inscription.update_all_carts(vipIns, groupe);
        }

        CartService.invalidateCache(bookingStore.$state);
        await bookingStore.compute_cart({ useCache: false });
    }

    /**
     * Effectue un checkout gratuit
     * 
     * @param {Object} bookingStore - Le store de réservation
     * @returns {Promise<boolean>} True si le checkout a réussi, false sinon
     */
    async postFreeCheckout(bookingStore) {
        this._prepareBookingStore(bookingStore);

        const post_request = await this._post(bookingStore);
        const paid_request = await this.setPaidFreeCheckout(bookingStore);

        return post_request && paid_request;
    }

    /**
     * Effectue un checkout administrateur
     * 
     * @param {Object} bookingStore - Le store de réservation
     * @param {Object} paiement_admin - Les informations de paiement administrateur
     * @returns {Promise<Object>} La réponse de la requête
     */
    async postAdminCheckout(bookingStore, paiement_admin) {
        bookingStore.paiement_admin = paiement_admin;
        bookingStore.generate_uuid();
        await this._refreshCartsBeforePost(bookingStore);
        const post_request = await this._postAdmin(bookingStore);
        return post_request;
    }

    /**
     * Effectue un checkout par défaut (avec paiement)
     * 
     * @param {Object} bookingStore - Le store de réservation
     * @returns {Promise<boolean>} True si le checkout a réussi, false sinon
     */
    async postDefaultCheckout(bookingStore) {
        this._prepareBookingStore(bookingStore);
        const post_request = await this._post(bookingStore);
        return post_request;
    }

    /**
     * Effectue un checkout gratuit pour un changement de parcours
     * 
     * @param {Object} bookingStore - Le store de réservation
     * @returns {Promise<boolean>} True si le checkout a réussi, false sinon
     */
    async postFreeChangeParcoursCheckout(bookingStore) {
        this._prepareBookingStore(bookingStore);

        const post_request = await this._post(bookingStore);
        const paid_request = await this.setPaidFreeCheckoutChangementParcours(bookingStore);

        return post_request && paid_request;
    }

    /**
     * Effectue un transfert d'inscription
     * 
     * @param {Object} bookingStore - Le store de réservation
     * @param {Object} transferable_inscription - L'inscription transférable
     * @returns {Promise<Object>} La réponse de la requête
     */
    async postTransfertInscription(bookingStore, transferable_inscription) {
        this._prepareBookingStore(bookingStore);

        let res = await UserService.postTransfertInscription(bookingStore.$state, transferable_inscription);
        return res;
    }

    /**
     * Marque un checkout gratuit comme payé
     * 
     * @param {Object} bookingStore - Le store de réservation
     * @returns {Promise<boolean>} True si l'opération a réussi, false sinon
     */
    async setPaidFreeCheckout(bookingStore) {
        return this._setPaidFreeCheckout(bookingStore, 'setPaidFreeCheckout');
    }

    /**
     * Marque un checkout gratuit de changement de parcours comme payé
     * 
     * @param {Object} bookingStore - Le store de réservation
     * @returns {Promise<boolean>} True si l'opération a réussi, false sinon
     */
    async setPaidFreeCheckoutChangementParcours(bookingStore) {
        return this._setPaidFreeCheckout(bookingStore, 'setPaidFreeCheckoutChangementParcours');
    }

    /**
     * Méthode générique pour marquer un checkout gratuit comme payé
     * 
     * @private
     * @param {Object} bookingStore - Le store de réservation
     * @param {string} lyraMethod - Le nom de la méthode LyraService à appeler
     * @returns {Promise<boolean>} True si l'opération a réussi, false sinon
     */
    async _setPaidFreeCheckout(bookingStore, lyraMethod) {
        let res = await LyraService[lyraMethod](bookingStore.uuid);

        if (res) {
            this._setTransactionInfosFreeCheckout(bookingStore);
        }
        return res;
    }

    /**
     * Envoie une requête POST pour créer une nouvelle réservation
     * 
     * @private
     * @param {Object} bookingStore - Le store de réservation
     * @returns {Promise<boolean>} True si la création a réussi, false sinon
     */
    async _post(bookingStore) {
        const bookingSystemStore = useBookingSystemStore();

        await this._refreshCartsBeforePost(bookingStore);

        if (bookingSystemStore.groupe.default_fields && bookingSystemStore.groupe.is_inscription_groupe) {
            bookingStore.groupe = bookingSystemStore.groupe.$state;
        }

        let res = await UserService.postCommande(bookingStore.$state);
        if (res && res.id && res.uuid) {
            bookingStore.id = res.id;
            bookingStore.uuid = res.uuid;
            return true;
        }
        return false;
    }

    /**
     * Envoie une requête POST pour créer une réservation administrateur
     * 
     * @private
     * @param {Object} bookingStore - Le store de réservation
     * @returns {Promise<Object>} La réponse de la requête
     */
    async _postAdmin(bookingStore) {
        let res = await AdminService.postCommande(bookingStore.$state);
        return res;
    }

    /**
     * Définit les informations de transaction pour un checkout gratuit
     * 
     * @private
     * @param {Object} bookingStore - Le store de réservation
     */
    _setTransactionInfosFreeCheckout(bookingStore) {
        if (!bookingStore.transaction) bookingStore.transaction = {};
        bookingStore.transaction.commande_infos = { orderId: bookingStore.uuid, amount: 0 };
    }
}

export default new CheckoutService();

